import { FC, ReactNode, useMemo } from 'react';
import { ListFilter } from 'lucide-react';

import { DropdownOptionAvatar, DropdownOptionContent } from '@/shared';
import { PlatformSelect as Select } from '@/shared/components/forms';
import EmptyView from '@/shared/components/layouts/views/EmptySection';

import type { ListFilterOption, ListFilterValue } from '../..';
import { normalizeListFilterValues } from '../../listFilterLogic';
import { normalizeListFilterOptions, optionKey } from './filterValueOptions';

interface FilterValueSelectProps {
    fallbackIcon?: ReactNode;
    fieldLabel: string;
    multiple?: boolean;
    onChange?: (value: ListFilterValue) => void;
    options: ListFilterOption[];
    placeholder?: string;
    value?: ListFilterValue;
}

const compactOptionLabel = (option: ListFilterOption, fallbackIcon?: ReactNode): ReactNode => (
    <span
        style={{
            alignItems: 'center',
            display: 'inline-flex',
            gap: '0.375rem',
            lineHeight: 1,
            maxWidth: '100%',
            minWidth: 0,
        }}
    >
        {option.avatar ? (
            <DropdownOptionAvatar
                {...option.avatar}
                props={{ ...option.avatar.props, size: 16 }}
                style={{ flex: 'none', ...option.avatar.style }}
            />
        ) : (option.icon ?? fallbackIcon) ? (
            <span style={{ alignItems: 'center', display: 'inline-flex', flex: 'none', lineHeight: 0 }}>
                {option.icon ?? fallbackIcon}
            </span>
        ) : null}
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {option.label}
        </span>
    </span>
);

const FilterValueSelect: FC<FilterValueSelectProps> = ({
    fallbackIcon,
    fieldLabel,
    multiple = true,
    onChange,
    options,
    placeholder = 'Select Values...',
    value,
}) => {
    const normalizedOptions = useMemo(() => normalizeListFilterOptions(options), [options]);
    const optionByKey = useMemo(
        () => new Map(normalizedOptions.map((option) => [optionKey(option.value), option])),
        [normalizedOptions],
    );
    const normalizedValue = useMemo(() => {
        const selectedValues = normalizeListFilterValues(value).filter((item) => optionByKey.has(optionKey(item)));

        return multiple ? selectedValues : selectedValues[0];
    }, [multiple, optionByKey, value]);

    return (
        <Select
            filterOption={(input, option) => {
                const resolved = option?.value === undefined ? undefined : optionByKey.get(optionKey(option.value));
                const searchText = resolved?.searchText ?? (typeof resolved?.label === 'string' ? resolved.label : '');

                return searchText.toLowerCase().includes(input.trim().toLowerCase());
            }}
            labelRender={({ label, value: selectedValue }) => {
                const resolved = optionByKey.get(optionKey(selectedValue as ListFilterOption['value']));

                return resolved ? compactOptionLabel(resolved, fallbackIcon) : label;
            }}
            mode={multiple ? 'multiple' : undefined}
            notFoundContent={
                <EmptyView
                    description={`No ${fieldLabel.toLowerCase()} values are available for this filter yet.`}
                    height='9rem'
                    icon={<ListFilter size='0.875rem' />}
                    label={`No ${fieldLabel} Values`}
                />
            }
            onChange={(nextValue) => onChange?.(nextValue as ListFilterValue)}
            optionRender={(option) => {
                const resolved = optionByKey.get(optionKey(option.value as ListFilterOption['value']));

                return resolved ? (
                    <DropdownOptionContent
                        avatar={resolved.avatar}
                        description={resolved.description}
                        icon={resolved.icon ?? fallbackIcon}
                        metadata={resolved.metadata}
                        title={resolved.label}
                    />
                ) : null;
            }}
            options={normalizedOptions}
            placeholder={placeholder}
            showSearch
            style={{ width: '100%' }}
            value={normalizedValue}
        />
    );
};

export default FilterValueSelect;
